import React, { useState, useRef, useEffect } from 'react';
import { colors } from '../utils/colors';
import type { Teacher } from '../utils/types';
import vosIcon from '../assects/VAV School Logo icon solid dark BG.svg';
import { login, verifyOtp, getTeacher, getTeacherClasses } from '../Api/auth';

interface Props {
  onLogin: (teacher: Teacher) => void;
}

const EyeIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.textLight}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </>
    )}
  </svg>
);

const inputStyle = (focused: boolean, hasError: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center',
  border: `1.5px solid ${hasError ? colors.dangerBorder : focused ? colors.primary : colors.border}`,
  borderRadius: 12, background: '#fff',
  padding: '0 16px', gap: 10,
  transition: 'border-color 0.2s',
});

const LoginScreen: React.FC<Props> = ({ onLogin }) => {
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused,  setPassFocused]  = useState(false);

  const [otp,            setOtp]            = useState(['', '', '', '', '', '']);
  const [otpError,       setOtpError]       = useState('');
  const [otpLoading,     setOtpLoading]     = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleCredentials = async () => {
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true); setError('');
    try {
      await login({ email: email.trim(), password });
      setStep('otp');
      setResendCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 300);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setOtp(['', '', '', '', '', '']);
    setOtpError('');
    setResendCooldown(60);
    otpRefs.current[0]?.focus();
    try {
      await login({ email: email.trim(), password });
    } catch {
      // silent
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otp]; next[index] = digit; setOtp(next); setOtpError('');
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) { setOtp(pasted.split('')); otpRefs.current[5]?.focus(); }
    e.preventDefault();
  };

  const handleVerifyOtp = async () => {
    const code = otp.join('');
    if (code.length < 6) { setOtpError('Enter the 6-digit OTP.'); return; }
    setOtpLoading(true); setOtpError('');
    try {
      const otpData = await verifyOtp({ email: email.trim(), otp: code });
      const profile = await getTeacher(otpData.teacher_id);

      const roleMap: Record<string, Teacher['role']> = {
        class_teacher: 'class_teacher',
        subject_teacher: 'subject_teacher',
        admin: 'admin',
         supervisor: 'supervisor',
      };

      let className: string | undefined;
      let sectionName: string | undefined;
      try {
        const mappings = await getTeacherClasses(profile.id);
        const mine = mappings.find(m => m.is_active && m.class_teacher_id === profile.id);
        if (mine) { className = mine.class_name; sectionName = mine.section_name; }
      } catch { /* ignore */ }

      const teacher: Teacher = {
        id: String(profile.id),
        name: [profile.first_name, profile.last_name].filter(Boolean).join(' '),
        subject: profile.expertise,
        role: roleMap[otpData.role] ?? 'subject_teacher',
        title: profile.title,
        email: profile.email,
        phone: profile.phone,
        address: profile.address,
        qualification: profile.highest_qualification,
        expertise: profile.expertise,
        otherExpertise: profile.other_expertise,
        dob: profile.dob,
        gender: profile.gender,
        photoPath: profile.teacher_photo,
        className,
        sectionName,
      };

      onLogin(teacher);
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Verification failed. Try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    }
    setOtpLoading(false);
  };

  const maskedEmail = email.replace(/(.{2})[^@]+(@.*)/, '$1***$2');

  /* ─── Credentials step ─── */
  if (step === 'credentials') return (
    <div style={{
      height: '100dvh',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',       /* no page scroll */
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Scrollable inner area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Logo + title */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          paddingTop: 72, paddingBottom: 32,
        }}>
          <img
            src={vosIcon}
            alt="VAV School Logo"
            width={110}
            height={110}
            style={{ borderRadius: 22 }}
          />
          <div style={{ fontSize: 28, fontWeight: 900, color: colors.primaryDark, marginTop: 14, letterSpacing: '-0.5px' }}>
            V.A.V School
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.primary, letterSpacing: '2px', marginTop: 4 }}>
            TEACHER  PORTAL
          </div>
        </div>

        {/* Form fields */}
        <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.8px', marginBottom: 8 }}>
              ENTER REGISTERED EMAIL
            </div>
            <div style={inputStyle(emailFocused, !!error)}>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                onKeyDown={e => e.key === 'Enter' && handleCredentials()}
                placeholder="yourname@gmail.com"
                style={{
                  flex: 1, border: 'none', outline: 'none',
                  fontSize: 15, padding: '15px 0',
                  background: 'transparent', color: colors.text,
                }}
              />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.8px', marginBottom: 8 }}>
              PASSWORD
            </div>
            <div style={inputStyle(passFocused, !!error)}>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                onFocus={() => setPassFocused(true)}
                onBlur={() => setPassFocused(false)}
                onKeyDown={e => e.key === 'Enter' && handleCredentials()}
                placeholder="Enter your password"
                style={{
                  flex: 1, border: 'none', outline: 'none',
                  fontSize: 15, padding: '15px 0',
                  background: 'transparent', color: colors.text,
                }}
              />
              <button onClick={() => setShowPass(s => !s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                <EyeIcon open={showPass} />
              </button>
            </div>
          </div>

          {error && (
            <div style={{
              background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
              borderRadius: 10, padding: '10px 14px',
              fontSize: 13, color: colors.danger,
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Spacer — pushes button area down inside scroll */}
        <div style={{ flex: 1 }} />
      </div>

      {/* Fixed-to-bottom Continue button — outside the scroll area */}
      <div style={{ padding: '12px 24px 36px', background: '#fff', flexShrink: 0 }}>
        <button
          onClick={handleCredentials}
          disabled={loading}
          style={{
            width: '100%', padding: '16px',
            background: loading ? colors.primaryLight : colors.primaryDark,
            color: '#fff', border: 'none', borderRadius: 14,
            fontSize: 16, fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {loading ? (
            <>
              <div style={{
                width: 16, height: 16, border: '2.5px solid rgba(255,255,255,0.4)',
                borderTopColor: '#fff', borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }}/>
              Please wait...
            </>
          ) : 'Continue'}
        </button>
        <div style={{ textAlign: 'center', fontSize: 12, color: colors.textLight, marginTop: 12 }}>
          Managed by VNEW Trust
        </div>
      </div>
    </div>
  );

  /* ─── OTP step ─── */
  return (
    <div style={{
      height: '100dvh',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',       /* no page scroll */
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Scrollable inner area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Back */}
        <div style={{ padding: '56px 24px 0' }}>
          <button
            onClick={() => { setStep('credentials'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 16, fontWeight: 500, color: colors.text, padding: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Back
          </button>
        </div>

        {/* Title */}
        <div style={{ padding: '28px 24px 0' }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: colors.text }}>Verify OTP</div>
          <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 6 }}>
            OTP sent to - Administrator
          </div>
        </div>

        {/* OTP boxes: 3 + dash + 3 */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 'min(10px, 2vw)', padding: '36px 24px 0',
          }}
          onPaste={handleOtpPaste}
        >
          {[0, 1, 2].map(i => (
            <input
              key={i}
              ref={el => { otpRefs.current[i] = el; }}
              type="text" inputMode="numeric" maxLength={1}
              value={otp[i]}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              style={{
                width: 'min(58px, 13vw)', height: 'min(62px, 14vw)', textAlign: 'center',
                fontSize: 'min(24px, 5.5vw)', fontWeight: 700,
                border: `2px solid ${otpError ? colors.dangerBorder : otp[i] ? colors.primary : colors.border}`,
                borderRadius: 14, background: '#fff', color: colors.text,
                outline: 'none', transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
            />
          ))}
          <div style={{ fontSize: 'min(22px, 5vw)', color: colors.textLight, fontWeight: 300, flexShrink: 0 }}>-</div>
          {[3, 4, 5].map(i => (
            <input
              key={i}
              ref={el => { otpRefs.current[i] = el; }}
              type="text" inputMode="numeric" maxLength={1}
              value={otp[i]}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              style={{
                width: 'min(58px, 13vw)', height: 'min(62px, 14vw)', textAlign: 'center',
                fontSize: 'min(24px, 5.5vw)', fontWeight: 700,
                border: `2px solid ${otpError ? colors.dangerBorder : otp[i] ? colors.primary : colors.border}`,
                borderRadius: 14, background: '#fff', color: colors.text,
                outline: 'none', transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
            />
          ))}
        </div>

        {otpError && (
          <div style={{
            margin: '16px 24px 0',
            background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
            borderRadius: 10, padding: '10px 14px',
            fontSize: 13, color: colors.danger, textAlign: 'center',
          }}>
            {otpError}
          </div>
        )}

        {/* Resend */}
        <div style={{ textAlign: 'right', padding: '16px 24px 0' }}>
          <button
            onClick={handleResendOtp}
            disabled={resendCooldown > 0}
            style={{
              background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600,
              color: resendCooldown > 0 ? colors.textLight : colors.primary,
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
          </button>
        </div>

        <div style={{ flex: 1 }} />
      </div>

      {/* Fixed-to-bottom Verify button — outside the scroll area */}
      <div style={{ padding: '12px 24px 36px', background: '#fff', flexShrink: 0 }}>
        <button
          onClick={handleVerifyOtp}
          disabled={otpLoading || otp.join('').length < 6}
          style={{
            width: '100%', padding: '16px',
            background: (otpLoading || otp.join('').length < 6) ? colors.primaryLight : colors.primaryDark,
            color: '#fff', border: 'none', borderRadius: 14,
            fontSize: 16, fontWeight: 700,
            cursor: (otpLoading || otp.join('').length < 6) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {otpLoading ? (
            <>
              <div style={{
                width: 16, height: 16, border: '2.5px solid rgba(255,255,255,0.4)',
                borderTopColor: '#fff', borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }}/>
              Verifying...
            </>
          ) : 'Verify'}
        </button>
        <div style={{ textAlign: 'center', fontSize: 12, color: colors.textLight, marginTop: 12 }}>
          Managed by VNEW Trust
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;